import * as React from 'react';

import { AddCloudDesktopIcon } from '@coursera/cds-icons';

import ExclusiveDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/ExclusiveDetailItem';
import IndustryCertificationDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/IndustryCertificationDetailItem';
import InstallationRequirementsDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/InstallationRequirementsDetailItem';
import ItemCountsDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/ItemCountsDetailItem';
import LanguagesDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/LanguagesDetailItem';
import LanguagesWithMLInfoDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/LanguagesWithMLInfoDetailItem';
import RecentlyUpdatedDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/RecentlyUpdatedDetailItem';
import ShareableCaseStudyDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/ShareableCaseStudyDetailItem';
import ShareableCertificateDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/ShareableCertificateDetailItem';
import StackabilityDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/StackabilityDetailItem';
import PAGE_TYPES from 'bundles/unified-description-page-common/constants/pageTypes';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import { ProductDetailItemNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import getVariantTitle from 'bundles/unified-description-page-common/utils/getVariantTitle';
import { isWithinSixMonths } from 'bundles/unified-description-page-common/utils/isWithinSixMonths';

import _t from 'i18n!nls/xdp';

type PageData =
  | ConsumerSpecializationPageFieldsFragment
  | EnterpriseSpecializationPageFieldsFragment
  | ConsumerCoursePageFieldsFragment
  | EnterpriseCoursePageFieldsFragment;

const getProductDetailItems = (
  pageData: Pick<
    PageData,
    'launchedAt' | 'primaryLanguages' | 'subtitleLanguages' | 'translatedLanguages' | 'dubbedLanguages'
  > & {
    isExclusive?: EnterpriseCoursePageFieldsFragment['isExclusive'];
    productTags?: EnterpriseSpecializationPageFieldsFragment['productTags'];
    productVariant?: ConsumerSpecializationPageFieldsFragment['productVariant'];
    itemCounts?: ConsumerSpecializationPageFieldsFragment['itemCounts'];
  },
  itemNames: Array<(typeof ProductDetailItemNames)[keyof typeof ProductDetailItemNames]>,
  pageType?: (typeof PAGE_TYPES)[keyof typeof PAGE_TYPES]
) => {
  if (itemNames.length === 0) {
    return [];
  }

  return itemNames
    .map((itemName) => {
      switch (itemName) {
        case ProductDetailItemNames.ShareableCertificate:
          return <ShareableCertificateDetailItem key={itemName} />;
        case ProductDetailItemNames.ShareableCaseStudy:
          return <ShareableCaseStudyDetailItem key={itemName} />;
        case ProductDetailItemNames.Exclusive:
          if (pageData.isExclusive) {
            return <ExclusiveDetailItem key={itemName} />;
          }
          return null;
        case ProductDetailItemNames.Languages:
          return (
            <LanguagesDetailItem
              primaryLanguages={pageData.primaryLanguages}
              subtitleLanguages={pageData.subtitleLanguages}
              key={itemName}
            />
          );
        case ProductDetailItemNames.LanguagesWithMLInfo:
          return (
            <LanguagesWithMLInfoDetailItem
              languages={{
                primaryLanguages: pageData.primaryLanguages,
                translatedLanguages: pageData.translatedLanguages,
                subtitleLanguages: pageData.subtitleLanguages || [],
                dubbedLanguages: pageData.dubbedLanguages,
              }}
              key={itemName}
            />
          );
        case ProductDetailItemNames.RecentlyUpdated:
          if (pageData.launchedAt && isWithinSixMonths(pageData.launchedAt)) {
            return <RecentlyUpdatedDetailItem launchedAt={pageData.launchedAt} key={itemName} />;
          }
          return null;
        case ProductDetailItemNames.IndustryCertification:
          if (
            pageData.productVariant &&
            pageData.productTags?.industryCertificationName &&
            pageData.productTags?.industryCertificationQualification
          ) {
            return (
              <IndustryCertificationDetailItem
                productType={getVariantTitle(pageData.productVariant)}
                industryCetificationQualification={pageData.productTags.industryCertificationQualification}
                industryCertificationName={pageData.productTags.industryCertificationName}
                key={itemName}
              />
            );
          }
          return null;
        case ProductDetailItemNames.InstallationRequirements:
          if (!pageType) return null;
          return (
            <InstallationRequirementsDetailItem
              subtitle={
                pageType === PAGE_TYPES.PORTFOLIO_PROJECT
                  ? _t('Use a Coursera Lab, a pre-configured in-browser cloud workspace (only available on desktop)')
                  : _t('Only available on desktop')
              }
              icon={<AddCloudDesktopIcon size="large" />}
              key={itemName}
            />
          );
        case ProductDetailItemNames.Stackability:
          return <StackabilityDetailItem />;
        case ProductDetailItemNames.ItemCounts:
          return <ItemCountsDetailItem itemCounts={pageData.itemCounts} key={itemName} />;
        default:
          return null;
      }
    })
    .filter((item) => item !== null);
};

export default getProductDetailItems;
